import type { IPowerbiPageDto } from 'dtos/powerbi-page.dto';
import type { IPowerbiReportDto } from 'dtos/powerbi-report.dto';
import type { IPowerbiTokenDto, IPowerbiTokenResponseDto } from 'dtos/powerbi-token.dto';
import type { IPowerbiWorkspaceDto } from 'dtos/powerbi-workspace.dto';
import { BackendGateway } from 'gateway/backend.gateway';
import { tokenize } from 'helpers/string.helper';

export class ReportsRepository {
  private static _instance: ReportsRepository | undefined;
  readonly CACHE_DURATION_MS = 5000; // 5 seconds
  private _lastFetchTime: number | undefined = undefined;
  private _tokenCache: IPowerbiTokenDto | undefined = undefined;

  private constructor(readonly backend: BackendGateway) {}

  static getInstance(backend: BackendGateway = BackendGateway.getInstance()): ReportsRepository {
    this._instance ??= new this(backend);
    return this._instance;
  }

  async getReportDetails(
    sessionId: string,
    details: { workspaceName: string; reportName: string; pageName: string },
  ): Promise<{ workspace: IPowerbiWorkspaceDto; report: IPowerbiReportDto; page: IPowerbiPageDto } | undefined> {
    const token = await this.getTokenDetails(sessionId);
    if (!token) {
      console.error('Error fetching token details');
      return;
    }

    const workspace =
      token.workspaces.find((workspace) => tokenize(workspace.name) === details.workspaceName) ?? token.workspaces[0];
    const report =
      workspace.reports.find((report) => tokenize(report.name) === details.reportName) ?? workspace.reports[0];
    const page = report.pages.find((page) => tokenize(page.displayName) === details.pageName) ?? report.pages[0];

    return { workspace, report, page };
  }

  async getTokenDetails(sessionId: string): Promise<IPowerbiTokenDto | undefined> {
    if (this._lastFetchTime && Date.now() - this._lastFetchTime < this.CACHE_DURATION_MS) {
      return this._tokenCache;
    }

    try {
      const tokenDetailsResponse = await this.backend.callOrThrow<IPowerbiTokenResponseDto>({
        uri: 'tokenDetails',
        params: { sessionId, force: this._tokenCache ? 0 : 1 },
      });
      this._lastFetchTime = Date.now();
      this._tokenCache = tokenDetailsResponse.token;
      if (!this._tokenCache) return undefined;

      return this._tokenCache;
    } catch (error) {
      console.error('Error fetching token details', error);
      return undefined;
    }
  }
}
